import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = logged in
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      // Clear any stale daily-quote cache so the very next visit fetches a
      // fresh random quote. Requirement: every new login must surface a
      // different quote from the previous session.
      try {
        for (const k of Object.keys(localStorage)) {
          if (k.startsWith("daily_quote_")) localStorage.removeItem(k);
        }
      } catch {
        // ignore — quote cache is best-effort.
      }
      const { data } = await api.post("/auth/login", { email, password });
      if (data?.token) localStorage.setItem("nsp_token", data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return {
        ok: false,
        error: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout request failed; clearing local session anyway", e);
    }
    localStorage.removeItem("nsp_token");
    try {
      for (const k of Object.keys(localStorage)) {
        if (k.startsWith("daily_quote_")) localStorage.removeItem(k);
      }
    } catch {
      // ignore
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
